<template>
  <section class="pad-t-0 admin-retailers-page">
    <div class="bb-container">
      <div class="back">
        <div class="back__back">
          <a @click.prevent="$router.back()">
            <div class="back__name">
              <img
                src="../../assets/img/ico/ico-arrow-back.svg"
                loading="lazy"
                alt="Volver"
                class="back__arrow"
              />
            </div>
          </a>
        </div>
      </div>

      <div class="g-edit" v-show="loaded">
        <div class="g-edit__header">
          <h1 class="g-edit__title">Retailers</h1>
        </div>

        <div class="retailers-toolbar">
          <div class="bb-form-item retailers-toolbar__search">
            <input
              v-model="searchQuery"
              class="bb-form"
              type="text"
              placeholder="Buscar por nombre o dominio..."
            />
          </div>
          <button
            type="button"
            class="button button--primary"
            @click="openCreateRetailerModal"
          >
            Nuevo retailer
          </button>
        </div>

        <div v-if="filteredRetailers.length === 0" class="retailers-empty">
          <p v-if="searchQuery">No hay retailers que coincidan con "{{ searchQuery }}".</p>
          <p v-else>Aún no hay retailers. Crea el primero con el botón "Nuevo retailer".</p>
        </div>

        <div v-else class="retailers-table">
          <div class="retailers-table__head">
            <div class="retailers-table__col retailers-table__col--logo">Logo</div>
            <div class="retailers-table__col retailers-table__col--name">Nombre</div>
            <div class="retailers-table__col retailers-table__col--key">Dominio</div>
            <div class="retailers-table__col retailers-table__col--actions">Acciones</div>
          </div>

          <div
            v-for="retailer in filteredRetailers"
            :key="retailer.id"
            class="retailers-table__row"
          >
            <div class="retailers-table__col retailers-table__col--logo">
              <div class="retailer-logo">
                <img
                  v-if="retailer.logoUrl"
                  :src="'https://images.hellobb.net/logos/' + retailer.logoUrl"
                  :alt="retailer.name"
                  class="retailer-logo__img"
                />
                <span v-else>{{ getRetailerInitials(retailer) }}</span>
              </div>
            </div>
            <div class="retailers-table__col retailers-table__col--name">
              <span class="retailers-table__label">Nombre:</span>
              {{ retailer.name }}
            </div>
            <div class="retailers-table__col retailers-table__col--key">
              <span class="retailers-table__label">Dominio:</span>
              {{ retailer.internalKey }}
            </div>
            <div class="retailers-table__col retailers-table__col--actions">
              <button
                type="button"
                class="button button--secondary button--small"
                @click="openEditRetailerModal(retailer)"
              >
                Editar
              </button>
              <button
                type="button"
                class="button button--danger button--small"
                @click="confirmDeleteRetailer(retailer)"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>

        <div v-if="errorMsg" class="bb-form-error mar-t-2">
          {{ errorMsg }}
        </div>
      </div>

      <div v-if="!loaded" class="mar-t-2">
        <loading-button></loading-button>
      </div>
    </div>

    <!-- Modal Crear / Editar Retailer -->
    <modal-dialog
      :show="showRetailerModal"
      @close="closeRetailerModal"
      target-class="modal__card--medium"
    >
      <div class="create-retailer-modal">
        <h3 class="header-3" style="margin-bottom: 24px;">
          {{ isEditingRetailer ? "Editar retailer" : "Crear nuevo retailer" }}
        </h3>

        <div class="edit-data">
          <div class="edit-data__item">
            <div class="bb-form-item">
              <div class="bb-form-label">Nombre *</div>
              <input
                v-model="retailerForm.name"
                class="bb-form"
                type="text"
                maxlength="200"
                placeholder="Ej: Nike, El Corte Inglés"
                @input="touch('name')"
              />
              <div class="bb-form-error" v-show="hasError('name')">
                Campo obligatorio
              </div>
            </div>
          </div>

          <div class="edit-data__item">
            <div class="bb-form-item">
              <div class="bb-form-label">Dominio (internalKey) *</div>
              <input
                v-model="retailerForm.internalKey"
                class="bb-form"
                type="text"
                maxlength="200"
                placeholder="Ej: nike, elcorteingles"
                :readonly="isEditingRetailer"
                :style="isEditingRetailer ? 'background: #f8f9fa; cursor: not-allowed;' : ''"
                @input="touch('internalKey')"
                @keypress="onInternalKeyKeyPress"
              />
              <div style="font-size: 0.875rem; color: #6c757d; margin-top: 0.25rem;">
                {{ isEditingRetailer
                  ? "El dominio no se puede modificar"
                  : "Solo letras minúsculas, números y guiones. Sin espacios." }}
              </div>
              <div class="bb-form-error" v-show="hasError('internalKey')">
                {{ internalKeyError }}
              </div>
            </div>
          </div>

          <div class="edit-data__item">
            <div class="bb-form-item">
              <div class="bb-form-label">Logo (opcional)</div>

              <!-- Logo actual (solo al editar) -->
              <div
                v-if="isEditingRetailer && currentRetailerLogoUrl && !retailerForm.logoPreview"
                style="margin-bottom: 12px;"
              >
                <div style="font-size: 0.875rem; color: #6c757d; margin-bottom: 8px;">Logo actual:</div>
                <img
                  :src="currentRetailerLogoUrl"
                  alt="Logo actual"
                  style="max-width: 120px; max-height: 120px; border: 1px solid #dee2e6; border-radius: 4px;"
                />
              </div>

              <input
                ref="logoInput"
                type="file"
                accept="image/*"
                @change="onLogoChange"
                class="bb-form"
                style="padding: 8px;"
              />

              <!-- Preview del nuevo logo -->
              <div v-if="retailerForm.logoPreview" style="margin-top: 12px;">
                <div style="font-size: 0.875rem; color: #6c757d; margin-bottom: 8px;">
                  {{ isEditingRetailer ? "Nuevo logo:" : "Preview:" }}
                </div>
                <img
                  :src="retailerForm.logoPreview"
                  alt="Preview"
                  style="max-width: 120px; max-height: 120px; border: 1px solid #dee2e6; border-radius: 4px;"
                />
                <button
                  type="button"
                  @click="clearLogo"
                  class="button button--link button--small"
                  style="display: block; margin-top: 8px;"
                >
                  {{ isEditingRetailer ? "Cancelar cambio de logo" : "Eliminar logo" }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div style="display: flex; gap: 12px; margin-top: 24px; justify-content: flex-end;">
          <button
            type="button"
            @click="closeRetailerModal"
            class="button button--secondary"
          >
            Cancelar
          </button>
          <loading-button v-if="savingRetailer"></loading-button>
          <button
            v-else
            type="button"
            @click="isEditingRetailer ? updateRetailer() : createRetailer()"
            class="button button--primary"
            :disabled="!retailerForm.name || !retailerForm.internalKey"
          >
            {{ isEditingRetailer ? "Guardar cambios" : "Crear retailer" }}
          </button>
        </div>

        <div v-if="modalError" class="bb-form-error" style="margin-top: 16px;">
          {{ modalError }}
        </div>
      </div>
    </modal-dialog>

    <!-- Modal Confirmación Eliminar -->
    <modal-dialog
      :show="showDeleteModal"
      @close="closeDeleteModal"
      target-class="modal__card--small"
    >
      <div class="create-retailer-modal">
        <h3 class="header-3" style="margin-bottom: 16px;">Eliminar retailer</h3>
        <p>
          ¿Seguro que quieres eliminar
          <strong>{{ retailerToDelete ? retailerToDelete.name : "" }}</strong>?
        </p>
        <p style="font-size: 0.875rem; color: #6c757d; margin-top: 8px;">
          Esta acción no se puede deshacer. Si el retailer está asociado a promociones,
          es posible que la operación falle.
        </p>

        <div style="display: flex; gap: 12px; margin-top: 24px; justify-content: flex-end;">
          <button
            type="button"
            @click="closeDeleteModal"
            class="button button--secondary"
          >
            Cancelar
          </button>
          <loading-button v-if="deletingRetailer"></loading-button>
          <button
            v-else
            type="button"
            @click="deleteRetailer"
            class="button button--danger"
          >
            Eliminar
          </button>
        </div>

        <div v-if="deleteError" class="bb-form-error" style="margin-top: 16px;">
          {{ deleteError }}
        </div>
      </div>
    </modal-dialog>
  </section>
</template>

<script>
import LoadingButton from "../../components/LoadingButton";
import ModalDialog from "../../components/ui/ModalDialog";
import { mapGetters } from "vuex";
import axios from "axios";

const API_BASE = process.env.API;

export default {
  name: "AdminRetailersPage",
  components: { LoadingButton, ModalDialog },
  data() {
    return {
      loaded: false,
      errorMsg: null,
      searchQuery: "",
      retailers: [],

      // Modal crear/editar
      showRetailerModal: false,
      isEditingRetailer: false,
      editingRetailerId: null,
      savingRetailer: false,
      modalError: null,
      touched: {},
      retailerForm: {
        name: "",
        internalKey: "",
        logoUrl: null,
        logoPreview: null,
      },

      // Modal eliminar
      showDeleteModal: false,
      retailerToDelete: null,
      deletingRetailer: false,
      deleteError: null,
    };
  },
  computed: {
    ...mapGetters({ auth_token: "auth_token" }),
    filteredRetailers() {
      const q = this.searchQuery.trim().toLowerCase();
      const list = (this.retailers || []).filter(r => r.internalKey);

      if (!q) {
        return [...list].sort((a, b) =>
          (a.name || "").localeCompare(b.name || "")
        );
      }

      return list
        .filter(r =>
          (r.name || "").toLowerCase().includes(q) ||
          (r.internalKey || "").toLowerCase().includes(q)
        )
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    },
    currentRetailerLogoUrl() {
      if (!this.isEditingRetailer || !this.retailerForm.logoUrlExisting) {
        return null;
      }
      return "https://images.hellobb.net/logos/" + this.retailerForm.logoUrlExisting;
    },
    internalKeyError() {
      if (!this.retailerForm.internalKey) return "Campo obligatorio";
      if (/\s/.test(this.retailerForm.internalKey)) {
        return "El dominio no puede contener espacios en blanco";
      }
      if (!/^[a-z0-9-]+$/.test(this.retailerForm.internalKey)) {
        return "Solo letras minúsculas, números y guiones";
      }
      return "";
    },
  },
  methods: {
    touch(field) {
      this.$set(this.touched, field, true);
    },

    hasError(field) {
      if (!this.touched[field]) return false;
      if (field === "name") return !this.retailerForm.name;
      if (field === "internalKey") return !!this.internalKeyError;
      return false;
    },

    getRetailerInitials(retailer) {
      if (!retailer || !retailer.name) return "??";
      const words = retailer.name.split(" ");
      if (words.length >= 2) {
        return words[0][0].toUpperCase() + words[1][0].toUpperCase();
      }
      return retailer.name.substring(0, 2).toUpperCase();
    },

    onInternalKeyKeyPress(event) {
      const allowedControlKeys = [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "Tab",
      ];

      if (allowedControlKeys.includes(event.key)) {
        return;
      }

      if (event.key === " ") {
        event.preventDefault();
        return;
      }

      // Convertir mayúsculas a minúsculas
      if (/^[A-Z]$/.test(event.key)) {
        event.preventDefault();

        const start = event.target.selectionStart;
        const end = event.target.selectionEnd;
        const value = event.target.value;
        const newValue =
          value.slice(0, start) + event.key.toLowerCase() + value.slice(end);

        event.target.value = newValue;
        event.target.setSelectionRange(start + 1, start + 1);

        this.retailerForm.internalKey = newValue;
        return;
      }

      // Permitir solo a-z, 0-9 y -
      if (!/^[a-z0-9-]$/.test(event.key)) {
        event.preventDefault();
      }
    },

    // ---------- Crear / Editar ----------
    openCreateRetailerModal() {
      this.isEditingRetailer = false;
      this.editingRetailerId = null;
      this.retailerForm = {
        name: "",
        internalKey: "",
        logoUrl: null,
        logoPreview: null,
        logoUrlExisting: null,
      };
      this.touched = {};
      this.modalError = null;
      this.showRetailerModal = true;
    },

    openEditRetailerModal(retailer) {
      this.isEditingRetailer = true;
      this.editingRetailerId = retailer.id;
      this.retailerForm = {
        name: retailer.name || "",
        internalKey: retailer.internalKey || "",
        logoUrl: null, // Solo cambia si se sube una nueva
        logoPreview: null,
        logoUrlExisting: retailer.logoUrl || null,
      };
      this.touched = {};
      this.modalError = null;
      this.showRetailerModal = true;
    },

    closeRetailerModal() {
      this.showRetailerModal = false;
      this.isEditingRetailer = false;
      this.editingRetailerId = null;
      this.retailerForm = {
        name: "",
        internalKey: "",
        logoUrl: null,
        logoPreview: null,
        logoUrlExisting: null,
      };
      this.touched = {};
      this.modalError = null;
      if (this.$refs.logoInput) {
        this.$refs.logoInput.value = "";
      }
    },

    onLogoChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        this.modalError = "Por favor selecciona una imagen válida";
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        this.modalError = "La imagen no puede superar los 2MB";
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        this.retailerForm.logoUrl = e.target.result;
        this.retailerForm.logoPreview = e.target.result;
        this.modalError = null;
      };
      reader.onerror = () => {
        this.modalError = "Error al leer la imagen";
      };
      reader.readAsDataURL(file);
    },

    clearLogo() {
      this.retailerForm.logoUrl = null;
      this.retailerForm.logoPreview = null;
      if (this.$refs.logoInput) {
        this.$refs.logoInput.value = "";
      }
    },

    async createRetailer() {
      this.modalError = null;
      this.touch("name");
      this.touch("internalKey");

      if (!this.retailerForm.name) {
        this.modalError = "El nombre es obligatorio";
        return;
      }
      if (this.internalKeyError) {
        this.modalError = this.internalKeyError;
        return;
      }

      this.savingRetailer = true;

      try {
        const payload = {
          name: this.retailerForm.name,
          internalKey: this.retailerForm.internalKey,
          logoUrl: this.retailerForm.logoUrl,
        };

        const res = await axios.post(`${API_BASE}/retailers`, payload, {
          headers: { Authorization: `Bearer ${this.auth_token}` },
        });

        this.retailers.push(res.data);
        this.closeRetailerModal();
      } catch (e) {
        console.error("Error creating retailer:", e);
        this.modalError =
          e.response?.data?.message ||
          "No se pudo crear el retailer. Intenta de nuevo.";
      } finally {
        this.savingRetailer = false;
      }
    },

    async updateRetailer() {
      this.modalError = null;
      this.touch("name");

      if (!this.retailerForm.name) {
        this.modalError = "El nombre es obligatorio";
        return;
      }

      this.savingRetailer = true;

      try {
        const payload = {
          name: this.retailerForm.name,
          // Solo incluir logoUrl si se subió una nueva imagen
          ...(this.retailerForm.logoUrl && { logoUrl: this.retailerForm.logoUrl }),
        };

        const res = await axios.put(
          `${API_BASE}/retailers/${this.editingRetailerId}`,
          payload,
          { headers: { Authorization: `Bearer ${this.auth_token}` } }
        );

        const updated = res.data;
        const index = this.retailers.findIndex(r => r.id === this.editingRetailerId);
        if (index !== -1) {
          this.$set(this.retailers, index, updated);
        }

        this.closeRetailerModal();
      } catch (e) {
        console.error("Error updating retailer:", e);
        this.modalError =
          e.response?.data?.message ||
          "No se pudo actualizar el retailer. Intenta de nuevo.";
      } finally {
        this.savingRetailer = false;
      }
    },

    // ---------- Eliminar ----------
    confirmDeleteRetailer(retailer) {
      this.retailerToDelete = retailer;
      this.deleteError = null;
      this.showDeleteModal = true;
    },

    closeDeleteModal() {
      this.showDeleteModal = false;
      this.retailerToDelete = null;
      this.deleteError = null;
    },

    async deleteRetailer() {
      if (!this.retailerToDelete) return;

      this.deleteError = null;
      this.deletingRetailer = true;

      try {
        await axios.delete(
          `${API_BASE}/retailers/${this.retailerToDelete.id}`,
          { headers: { Authorization: `Bearer ${this.auth_token}` } }
        );

        this.retailers = this.retailers.filter(
          r => r.id !== this.retailerToDelete.id
        );
        this.closeDeleteModal();
      } catch (e) {
        console.error("Error deleting retailer:", e);
        this.deleteError =
          e.response?.data?.message ||
          "No se pudo eliminar el retailer. Puede que esté asociado a promociones.";
      } finally {
        this.deletingRetailer = false;
      }
    },

    // ---------- Carga ----------
    async loadRetailers() {
      try {
        const res = await this.$store.dispatch("getRetailers");
        this.retailers = res || [];
      } catch (e) {
        console.error("No se pudieron cargar los retailers", e);
        this.errorMsg = "No se pudieron cargar los retailers.";
        this.retailers = [];
      }
    },
  },
  async mounted() {
    await this.loadRetailers();
    this.loaded = true;
  },
};
</script>

<style scoped>
.retailers-toolbar {
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.retailers-toolbar__search {
  flex: 1;
  min-width: 240px;
  margin-bottom: 0;
}

.retailers-empty {
  padding: 2rem;
  text-align: center;
  background: #f8f9fa;
  border: 1px dashed #dee2e6;
  border-radius: 4px;
  color: #6c757d;
}

.retailers-table {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  overflow: hidden;
  background: #fff;
}

.retailers-table__head {
  display: grid;
  grid-template-columns: 80px 1fr 1fr 200px;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  font-weight: 600;
  font-size: 0.875rem;
  color: #495057;
}

.retailers-table__row {
  display: grid;
  grid-template-columns: 80px 1fr 1fr 200px;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f1f3f5;
  align-items: center;
}

.retailers-table__row:last-child {
  border-bottom: none;
}

.retailers-table__row:hover {
  background: #fafbfc;
}

.retailers-table__col {
  display: flex;
  align-items: center;
}

.retailers-table__col--actions {
  gap: 0.5rem;
  justify-content: flex-end;
}

.retailers-table__col--key {
  color: #6c757d;
  font-size: 0.875rem;
}

.retailers-table__label {
  display: none;
  font-weight: 600;
  margin-right: 0.5rem;
  color: #495057;
}

.retailer-logo {
  width: 48px;
  height: 48px;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
  color: #495057;
  flex-shrink: 0;
  overflow: hidden;
}

.retailer-logo__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.create-retailer-modal {
  padding: 8px;
}

@media (max-width: 768px) {
  .retailers-table__head {
    display: none;
  }

  .retailers-table__row {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    padding: 1rem;
  }

  .retailers-table__col--logo {
    justify-content: flex-start;
  }

  .retailers-table__col--actions {
    justify-content: flex-start;
    margin-top: 0.5rem;
  }

  .retailers-table__label {
    display: inline;
  }
}
</style>
